// Presentational helpers for the shared collapsible Panel (lint split from Panel.jsx).
import { Box, Chip, Collapse, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

export function resolveDefaultOpen(isOpenProp, defaultOpenProp) {
  return isOpenProp !== undefined ? isOpenProp : defaultOpenProp;
}

export function resolvePanelOpen(
  collapsible,
  isControlled,
  open,
  internalOpen,
) {
  if (!collapsible) return true;
  return isControlled ? open : internalOpen;
}

/** forceOpen overlays visually; does not change saved/internal open state. */
export function resolveEffectiveOpen({
  collapsible,
  hideHeader,
  isControlled,
  open,
  internalOpen,
  forceOpen,
}) {
  const canCollapse = collapsible && !hideHeader;
  const baseOpen = resolvePanelOpen(
    canCollapse,
    isControlled,
    open,
    internalOpen,
  );
  return baseOpen || Boolean(forceOpen && canCollapse);
}

export function buildPanelTestId(testId, title) {
  return testId || `panel-${title?.toLowerCase().replace(/\s+/g, '-')}`;
}

export function handlePanelHeaderKeyDown(event, toggle) {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  if (event.key === ' ') event.preventDefault();
  toggle();
}

function renderChevron(isOpen) {
  return (
    <Box component="span" sx={{ color: 'text.subtle', fontSize: '0.8em' }}>
      {isOpen ? '▾' : '▸'}
    </Box>
  );
}

function renderCountBadge(count) {
  return (
    <Chip
      label={count}
      size="small"
      variant="outlined"
      sx={{
        ml: 'auto',
        height: 22,
        fontSize: '0.75rem',
        fontWeight: 600,
        color: 'text.secondary',
        borderColor: 'divider',
        '& .MuiChip-label': { px: 1 },
      }}
    />
  );
}

function renderHeaderActions({
  headerActions,
  showEditIcon,
  onEditClick,
  title,
  hasCount,
}) {
  if (!headerActions && !showEditIcon) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        ml: hasCount ? 0 : 'auto',
      }}
    >
      {headerActions}
      {showEditIcon ? (
        <IconButton
          size="small"
          onClick={onEditClick}
          aria-label={`Edit ${title}`}
        >
          <EditIcon fontSize="inherit" />
        </IconButton>
      ) : null}
    </Box>
  );
}

export function renderPanelContent({
  collapsible,
  isOpen,
  testId,
  contentClassName,
  flat,
  children,
}) {
  const content = (
    <Box
      id={`${testId}-content`}
      data-testid={`${testId}-content`}
      className={contentClassName}
      sx={{ p: flat ? 0 : 1 }}
    >
      {children}
    </Box>
  );

  if (!collapsible) return content;
  return <Collapse in={isOpen}>{content}</Collapse>;
}

function resolveHeaderTabIndex(headerTabIndex, collapsible) {
  if (headerTabIndex !== undefined) return headerTabIndex;
  return collapsible ? 0 : undefined;
}

export function renderPanelHeader({
  collapsible,
  isOpen,
  contentId,
  toggle,
  onKeyDown,
  title,
  hasCount,
  count,
  headerActions,
  showEditIcon,
  onEditClick,
  headerTabIndex,
}) {
  const resolvedHeaderTabIndex = resolveHeaderTabIndex(
    headerTabIndex,
    collapsible,
  );

  return (
    <Box
      onClick={collapsible ? toggle : undefined}
      onKeyDown={collapsible ? onKeyDown : undefined}
      role={collapsible ? 'button' : undefined}
      tabIndex={resolvedHeaderTabIndex}
      aria-label={collapsible ? title : undefined}
      aria-expanded={collapsible ? isOpen : undefined}
      aria-controls={collapsible ? contentId : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 2,
        fontSize: '0.95em',
        fontWeight: 700,
        cursor: collapsible ? 'pointer' : 'default',
        bgcolor: 'background.subtle',
        borderBottom: 1,
        borderColor: 'divider',
        borderRadius: '4px 4px 0 0',
        ...(collapsible && {
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 2,
          },
        }),
      }}
    >
      {collapsible ? renderChevron(isOpen) : null}
      <Box component="h3" sx={{ m: 0, font: 'inherit' }}>
        {title}
      </Box>
      {hasCount ? renderCountBadge(count) : null}
      {renderHeaderActions({
        headerActions,
        showEditIcon,
        onEditClick,
        title,
        hasCount,
      })}
    </Box>
  );
}
